import React, { useEffect, useMemo, useRef } from "react";

import { cn } from "@/lib/utils";
import type { StrategyRdMatch, StrategyRdOhlcBar } from "../../../types/strategyRd";
import type { StrategyRdConceptDrawing } from "../../../utils/strategyRdConceptDrawings";
import {
  strategyRdConceptDrawings,
  strategyRdMergeLevelSegments,
} from "../../../utils/strategyRdConceptDrawings";
import { strategyRdConceptFrameBarTimes } from "../../../utils/strategyRdConceptViewport";
import { SL_COLOR, TP_COLOR } from "../../../utils/executionTradeOverlayLevels";
import {
  strategyRdMatchDisplayDirection,
  strategyRdPathReadyEnter,
  strategyRdPathEventsNormalized,
} from "../../../utils/strategyRdPath";
import { patternBarPalette } from "../../../utils/vela/tesseraPatternBarHighlight";

const THUMB_W = 280;
const THUMB_H = 96;
const PAD = 2;
/** Match list snippet — same framing as chart zoom (10 before, 80 after signal). */
const BARS_BEFORE_SETUP = 10;
const BARS_AFTER_SIGNAL = 80;
const WINDOW_BARS = BARS_BEFORE_SETUP + 1 + BARS_AFTER_SIGNAL;

/** Chart candle palette (Strategy Lab / Trade Review grey bodies). */
const CANDLE_UP = "#8E97A6";
const CANDLE_DOWN = "#374151";

/** Match chart position-tool fills (same as tesseraTradeMarkerEmit). */
const ENTRY_LINE_COLOR = "#7DD3E8";
const RISK_FILL = "rgba(224, 1, 1, 0.10)";
const REWARD_FILL = "rgba(6, 227, 99, 0.10)";

export type StrategyRdMatchThumbLevels = {
  entry: number;
  stop: number;
  target: number;
  direction: "long" | "short";
};

function finitePrice(value: number | null | undefined): number | null {
  return value != null && Number.isFinite(value) ? value : null;
}

/**
 * Drop share-volume / pane magnitudes that would crush candle Y in the snippet.
 * Mirrors tesseraConceptOverlayEmit's volume clamp (barHigh * 50 / >10k).
 */
function thumbPriceFitsCandleRange(
  price: number,
  candleMin: number,
  candleMax: number
): boolean {
  if (!Number.isFinite(price)) return false;
  const hi = Math.max(Math.abs(candleMin), Math.abs(candleMax), 1);
  if (Math.abs(price) > hi * 50 && Math.abs(price) > 10_000) return false;
  const mid = (candleMin + candleMax) / 2;
  const span = Math.max(candleMax - candleMin, Math.abs(mid) * 0.01, 1);
  if (Math.abs(price - mid) > span * 50) return false;
  return true;
}

/** Price span for the snippet — candles, drawings, and live Entry/SL/TP. */
export function strategyRdMatchThumbPriceSpan(
  bars: readonly StrategyRdOhlcBar[],
  drawings: readonly StrategyRdConceptDrawing[],
  levels?: StrategyRdMatchThumbLevels | null
): { min: number; max: number } | null {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const b of bars) {
    if (Number.isFinite(b.low)) min = Math.min(min, b.low);
    if (Number.isFinite(b.high)) max = Math.max(max, b.high);
  }
  const candleMin = min;
  const candleMax = max;
  const include = (price: number | null | undefined) => {
    if (price == null || !Number.isFinite(price)) return;
    if (
      Number.isFinite(candleMin) &&
      Number.isFinite(candleMax) &&
      candleMax >= candleMin &&
      !thumbPriceFitsCandleRange(price, candleMin, candleMax)
    ) {
      return;
    }
    min = Math.min(min, price);
    max = Math.max(max, price);
  };
  for (const d of drawings) {
    // Volume / oscillator panes use non-price Y — never expand the price snippet.
    if (d.paneId && d.paneId !== "price") continue;
    include(d.high);
    include(d.low);
    include(d.price);
    include(d.fromPrice);
    include(d.toPrice);
  }
  include(levels?.entry);
  include(levels?.stop);
  include(levels?.target);
  if (!(max > min) && bars[0] && Number.isFinite(bars[0].close)) {
    min = bars[0].close;
    max = min + 1;
  }
  if (!(max > min)) return null;
  return { min, max };
}

function parseMs(raw: string | null | undefined): number | null {
  const t = (raw || "").trim().replace(/\+00:00$/, "Z");
  if (!t) return null;
  const ms = Date.parse(t);
  return Number.isFinite(ms) ? ms : null;
}

function nearestIndex(timesMs: readonly number[], target: number): number {
  let best = 0;
  let bestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < timesMs.length; i += 1) {
    const d = Math.abs(timesMs[i]! - target);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

/**
 * First bar from entry where SL or TP is touched — both zones share this end
 * (same rule as chart `firstSlOrTpTouchEndMs`).
 */
export function strategyRdMatchThumbZoneEndIndex(
  bars: readonly StrategyRdOhlcBar[],
  entryIdx: number,
  isLong: boolean,
  stop: number | null,
  target: number | null
): number {
  if (!bars.length) return 0;
  const start = Math.max(0, Math.min(entryIdx, bars.length - 1));
  for (let i = start; i < bars.length; i += 1) {
    const b = bars[i]!;
    let hit = false;
    if (stop != null && Number.isFinite(stop)) {
      hit = isLong ? b.low <= stop : b.high >= stop;
    }
    if (!hit && target != null && Number.isFinite(target)) {
      hit = isLong ? b.high >= target : b.low <= target;
    }
    if (hit) return i;
  }
  return bars.length - 1;
}

const thumbTimesCache = new WeakMap<object, number[]>();

function thumbTimes(candles: readonly StrategyRdOhlcBar[]): number[] {
  let timesMs = thumbTimesCache.get(candles);
  if (!timesMs) {
    // Once per candle list: parsed per thumbnail, a long window made the list slow.
    timesMs = candles.map((c) => parseMs(c.timestamp) ?? Number.NaN);
    thumbTimesCache.set(candles, timesMs);
  }
  return timesMs;
}

function thumbSetupMs(match: StrategyRdMatch): number | null {
  const focus = strategyRdConceptFrameBarTimes(match)
    .map((t) => parseMs(t))
    .filter((n): n is number => n != null);
  return parseMs(match.path?.setup_bar_time) ?? focus[0] ?? parseMs(match.bar_time);
}

/**
 * Whether these candles reach this setup. The chart loads the latest stretch of the window, so
 * an older setup's thumbnail would otherwise snap to the first candles — someone else's chart.
 */
export function strategyRdMatchThumbCovers(candles: readonly StrategyRdOhlcBar[], match: StrategyRdMatch): boolean {
  if (candles.length < 2) return false;
  const setupMs = thumbSetupMs(match);
  if (setupMs == null) return false;
  const times = thumbTimes(candles);
  const first = times[0]!;
  const last = times[times.length - 1]!;
  const step = (last - first) / (times.length - 1);
  return setupMs >= first - step && setupMs <= last + step;
}

export function strategyRdMatchThumbSlice(
  candles: readonly StrategyRdOhlcBar[],
  match: StrategyRdMatch,
  windowBars = WINDOW_BARS,
  barsBeforeSetup = BARS_BEFORE_SETUP
): StrategyRdOhlcBar[] {
  if (!candles.length) return [];
  const timesMs = thumbTimes(candles);
  const setupMs = thumbSetupMs(match);
  if (setupMs == null) return candles.slice(0, Math.min(windowBars, candles.length));
  const setupIdx = nearestIndex(timesMs, setupMs);
  let from = Math.max(0, setupIdx - barsBeforeSetup);
  let to = Math.min(candles.length, from + windowBars);
  from = Math.max(0, to - windowBars);
  return candles.slice(from, to);
}

function paintThumb(
  ctx: CanvasRenderingContext2D,
  bars: readonly StrategyRdOhlcBar[],
  drawings: readonly StrategyRdConceptDrawing[],
  washBarTimes: readonly string[],
  direction: StrategyRdMatch["direction"],
  entryBarTime: string | null | undefined,
  levels?: StrategyRdMatchThumbLevels | null
): void {
  const w = THUMB_W;
  const h = THUMB_H;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = "rgba(125, 211, 232, 0.06)";
  ctx.fillRect(0, 0, w, h);
  if (!bars.length) return;

  const span = strategyRdMatchThumbPriceSpan(bars, drawings, levels);
  let min = span?.min ?? bars[0]!.close;
  let max = span?.max ?? min + 1;
  const padY = (max - min) * 0.08;
  min -= padY;
  max += padY;
  const plotW = w - PAD * 2;
  const plotH = h - PAD * 2;
  const slot = plotW / bars.length;
  const yOf = (price: number) => PAD + ((max - price) / (max - min)) * plotH;
  const timesMs = bars.map((b) => parseMs(b.timestamp) ?? Number.NaN);

  const xOfTime = (raw: string | null | undefined): number | null => {
    const ms = parseMs(raw);
    if (ms == null) return null;
    const idx = nearestIndex(timesMs, ms);
    return PAD + idx * slot + slot * 0.5;
  };

  const washIdx = new Set<number>();
  for (const raw of washBarTimes) {
    const ms = parseMs(raw);
    if (ms == null) continue;
    washIdx.add(nearestIndex(timesMs, ms));
  }
  const palette = patternBarPalette(
    direction === "short" ? "short" : direction === "long" ? "long" : "neutral"
  );
  Array.from(washIdx).forEach((idx) => {
    const x = PAD + idx * slot + slot * 0.5;
    ctx.fillStyle = palette.fill;
    ctx.fillRect(x - slot * 0.55, PAD, Math.max(2, slot * 1.1), plotH);
  });

  for (const d of drawings) {
    if (d.paneId && d.paneId !== "price") continue;
    if (d.kind === "ray") {
      const y1 = d.fromPrice ?? d.high ?? d.price;
      const y2 = d.toPrice ?? d.low ?? d.price;
      const x0 = xOfTime(d.fromBarTime || d.barTime);
      const x1 = xOfTime(d.toBarTime || d.barTime);
      if (y1 == null || y2 == null || x0 == null || x1 == null) continue;
      ctx.strokeStyle = "rgba(56, 187, 209, 0.9)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(x0, yOf(y1));
      ctx.lineTo(x1, yOf(y2));
      ctx.stroke();
    } else if (d.kind === "zone" && d.high != null && d.low != null && d.high > d.low) {
      const x0 = xOfTime(d.fromBarTime || d.barTime);
      const x1 = xOfTime(d.toBarTime || d.barTime);
      if (x0 == null || x1 == null) continue;
      const left = Math.min(x0, x1) - slot * 0.35;
      const right = Math.max(x0, x1) + slot * 0.35;
      ctx.fillStyle = "rgba(125, 211, 232, 0.28)";
      ctx.fillRect(left, yOf(d.high), Math.max(2, right - left), Math.max(2, yOf(d.low) - yOf(d.high)));
    } else if (d.kind === "level") {
      const price = d.price ?? (d.high != null && d.low != null ? (d.high + d.low) / 2 : null);
      if (price == null) continue;
      const y = yOf(price);
      const x0 = xOfTime(d.fromBarTime) ?? (d.fromBarTime || d.toBarTime ? null : PAD);
      const x1 = xOfTime(d.toBarTime) ?? (d.fromBarTime || d.toBarTime ? null : w - PAD);
      // Spanned pool rails stay Take→Enter; unspanned levels stay chart-wide.
      if (x0 == null || x1 == null) continue;
      const spanned = Boolean(d.fromBarTime || d.toBarTime);
      ctx.strokeStyle = spanned ? "rgba(56, 187, 209, 0.95)" : "rgba(56, 187, 209, 0.7)";
      ctx.lineWidth = spanned ? 2 : 1;
      ctx.beginPath();
      ctx.moveTo(Math.min(x0, x1), y);
      ctx.lineTo(Math.max(x0, x1), y);
      ctx.stroke();
    } else if (d.kind === "marker" && d.price != null) {
      const x = xOfTime(d.barTime);
      if (x == null) continue;
      ctx.fillStyle = "rgba(56, 187, 209, 0.9)";
      ctx.beginPath();
      ctx.arc(x, yOf(d.price), Math.max(1.5, slot * 0.22), 0, Math.PI * 2);
      ctx.fill();
    }
  }

  for (let i = 0; i < bars.length; i += 1) {
    const b = bars[i]!;
    const x = PAD + i * slot + slot * 0.5;
    const up = b.close >= b.open;
    const color = up ? CANDLE_UP : CANDLE_DOWN;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, yOf(b.high));
    ctx.lineTo(x, yOf(b.low));
    ctx.stroke();
    const bodyTop = yOf(Math.max(b.open, b.close));
    const bodyBot = yOf(Math.min(b.open, b.close));
    const bw = Math.max(1.5, slot * 0.55);
    ctx.fillRect(x - bw / 2, bodyTop, bw, Math.max(1, bodyBot - bodyTop));
  }

  const entry = finitePrice(levels?.entry);
  const stop = finitePrice(levels?.stop);
  const target = finitePrice(levels?.target);
  const isLong = (levels?.direction ?? direction) !== "short";
  if (entry != null && (stop != null || target != null)) {
    const entryMs = parseMs(entryBarTime);
    const entryIdx =
      entryMs != null ? nearestIndex(timesMs, entryMs) : Math.max(0, bars.length - 1);
    const endIdx = strategyRdMatchThumbZoneEndIndex(bars, entryIdx, isLong, stop, target);
    const x0 = PAD + entryIdx * slot + slot * 0.15;
    const x1 = PAD + endIdx * slot + slot * 0.85;
    const left = Math.min(x0, x1);
    const right = Math.max(x0, x1);
    const width = Math.max(2, right - left);
    const yEntry = yOf(entry);

    if (stop != null && stop !== entry) {
      const yStop = yOf(stop);
      const top = Math.min(yEntry, yStop);
      const height = Math.max(2, Math.abs(yStop - yEntry));
      ctx.fillStyle = RISK_FILL;
      ctx.fillRect(left, top, width, height);
      ctx.strokeStyle = SL_COLOR;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(left, yStop);
      ctx.lineTo(right, yStop);
      ctx.stroke();
    }
    if (target != null && target !== entry) {
      const yTarget = yOf(target);
      const top = Math.min(yEntry, yTarget);
      const height = Math.max(2, Math.abs(yTarget - yEntry));
      ctx.fillStyle = REWARD_FILL;
      ctx.fillRect(left, top, width, height);
      ctx.strokeStyle = TP_COLOR;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(left, yTarget);
      ctx.lineTo(right, yTarget);
      ctx.stroke();
    }
    ctx.strokeStyle = ENTRY_LINE_COLOR;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(left, yEntry);
    ctx.lineTo(right, yEntry);
    ctx.stroke();
  }
}

export type StrategyRdMatchThumbProps = {
  match: StrategyRdMatch;
  candles: readonly StrategyRdOhlcBar[];
  candleType: string;
  setupId?: string | null;
  confluences?: readonly (string | { id: string; candle_type?: string | null })[] | null;
  /** Live Rules Entry/SL/TP — chart-style red/green boxes until first hit. */
  levels?: StrategyRdMatchThumbLevels | null;
  className?: string;
};

export function StrategyRdMatchThumb({
  match,
  candles,
  candleType,
  setupId = null,
  confluences = null,
  levels = null,
  className,
}: StrategyRdMatchThumbProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const slice = useMemo(
    () => strategyRdMatchThumbSlice(candles, match),
    [candles, match]
  );
  const drawings = useMemo(
    () =>
      strategyRdMergeLevelSegments(
        strategyRdConceptDrawings({
          match,
          setupId,
          confluences,
          candles: slice,
          chartCandleType: candleType,
          setupCandleType: candleType,
        })
      ),
    [candleType, confluences, match, setupId, slice]
  );
  const direction = strategyRdMatchDisplayDirection(match);
  const entryBarTime = strategyRdPathReadyEnter(match.path)?.barTime ?? null;
  const washBarTimes = useMemo(() => {
    const events = strategyRdPathEventsNormalized(match.path);
    // Incomplete paths may still wash setup — never treat entry as the fill bar.
    const pathReady = match.path?.status === "ready";
    const times = events
      .filter((e) => pathReady || e.kind !== "entry")
      .map((e) => e.bar_time)
      .filter(Boolean);
    if (times.length === 0) {
      const fallback = match.path?.setup_bar_time || match.bar_time;
      return fallback ? [fallback] : [];
    }
    return times;
  }, [match]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    canvas.width = THUMB_W * dpr;
    canvas.height = THUMB_H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    paintThumb(ctx, slice, drawings, washBarTimes, direction, entryBarTime, levels);
  }, [direction, drawings, entryBarTime, levels, slice, washBarTimes]);

  return (
    <canvas
      ref={canvasRef}
      width={THUMB_W}
      height={THUMB_H}
      aria-hidden
      className={cn("block h-24 w-full shrink-0 rounded-[4px]", className)}
      data-strategy-rd-match-thumb=""
    />
  );
}
